// Reference-overlay panel (OVL-1/2, E-40). Upload a PNG/JPEG, then align it to the
// world: opacity, XYZ nudge, hide/show, delete. The plane is client render chrome only
// (the sim never reads it). Earth-only in v1. Rejections (too large / unsupported /
// over-cap) surface as toasts through the controller (E-40).
import { h } from '../h';
import type { Game } from '../../boot';
import type { OverlayController } from '../../earth/overlay';

/** Read a File as a data URL. */
function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

/** Natural pixel size of an image data URL. */
function imageSize(dataUrl: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('decode failed'));
    img.src = dataUrl;
  });
}

export function openOverlayPanel(game: Game, controller: OverlayController): void {
  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-overlaypanel', 'data-testid': 'overlay-screen' });
  overlay.append(panel);
  document.body.append(overlay);

  const status = h('div', { class: 'bb-script-status bb-script-ok', 'data-testid': 'overlay-status' }, 'Upload a drone shot / plan, then nudge it into place.');
  const listEl = h('div', { class: 'bb-overlay-list' });

  const rebuild = (): void => {
    listEl.innerHTML = '';
    const items = controller.list();
    if (items.length === 0) { listEl.append(h('div', { class: 'bb-muted' }, 'No overlays yet.')); return; }
    items.forEach((p, i) => {
      const opacity = h('input', { type: 'range', min: '0', max: '1', step: '0.05', value: String(p.opacity), 'data-testid': `overlay-opacity-${i}` }) as HTMLInputElement;
      opacity.oninput = () => controller.setOpacity(p.overlayId, Number(opacity.value));
      const nudge = (label: string, dx: number, dy: number, dz: number): HTMLElement => {
        const b = h('button', { class: 'bb-btn' }, label) as HTMLButtonElement;
        b.onclick = () => controller.nudge(p.overlayId, dx, dy, dz);
        return b;
      };
      const hide = h('button', { class: 'bb-btn', 'data-testid': `overlay-hide-${i}` }, p.hidden ? 'Show' : 'Hide') as HTMLButtonElement;
      hide.onclick = () => { controller.toggleHidden(p.overlayId); rebuild(); };
      const del = h('button', { class: 'bb-btn', 'data-testid': `overlay-del-${i}` }, 'Delete') as HTMLButtonElement;
      del.onclick = () => { controller.remove(p.overlayId); rebuild(); };
      listEl.append(h('div', { class: 'bb-overlay-item' },
        h('div', { class: 'bb-overlay-name' }, `Overlay ${i + 1} · ${p.widthBlocks.toFixed(0)}b wide`),
        h('div', { class: 'bb-row' }, nudge('W', -1, 0, 0), nudge('E', 1, 0, 0), nudge('N', 0, 0, -1), nudge('S', 0, 0, 1), nudge('↓', 0, -1, 0), nudge('↑', 0, 1, 0)),
        h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Opacity'), opacity, hide, del),
      ));
    });
  };

  const file = h('input', { type: 'file', accept: 'image/png,image/jpeg', 'data-testid': 'overlay-file' }) as HTMLInputElement;
  file.onchange = async () => {
    const f = file.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await readDataUrl(f);
      const { w, h: ih } = await imageSize(dataUrl);
      const r = await controller.add(dataUrl, w, ih);
      status.textContent = r.ok ? 'Overlay added — nudge it into place.' : `Rejected: ${r.reason}`;
      rebuild();
    } catch {
      status.textContent = 'Could not read that image.';
    }
    file.value = '';
  };

  const close = (): void => { overlay.remove(); game.uiOpen = false; };
  const closeBtn = h('button', { class: 'bb-btn', 'data-testid': 'overlay-close' }, 'Close') as HTMLButtonElement;
  closeBtn.onclick = close;
  overlay.onclick = (e) => { if (e.target === overlay) close(); };

  // Bake to ground (OVL-4, M43): freeze the aligned overlays (+ any NAIP imagery) into the
  // surface as deterministic generator input — the ENG-1 crossing. Reloads the world.
  const baked = game.hasBakedImagery();
  const bake = h('button', { class: 'bb-btn', 'data-testid': 'overlay-bake' }, baked ? 'Un-bake imagery' : 'Bake to ground') as HTMLButtonElement;
  bake.onclick = () => {
    status.textContent = baked ? 'Un-baking — reloading…' : 'Baking imagery into the ground — reloading…';
    void (baked ? game.unbakeImagery() : game.bakeImagery());
  };

  panel.append(
    h('div', { class: 'bb-window-title' }, 'Reference Overlays'),
    status,
    h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Upload PNG/JPEG'), file),
    listEl,
    h('div', { class: 'bb-muted bb-overlay-bakehint' }, baked
      ? 'Imagery is baked into the ground. Un-bake to regenerate pristine chunks without it.'
      : 'Bake freezes the aligned image into the surface as real block colors (pristine chunks only; your builds are never touched).'),
    h('div', { class: 'bb-row' }, bake, closeBtn),
  );
  rebuild();
}
